// ─── OJT Hub — Match Score UI Components ─────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import { getMatchScore, scoreToRecommendation } from "./AIMatchEngine";

const T = {
  primary:"#0a6e4e", primaryL:"#e8f5f0",
  amber:"#d97706",   amberL:"#fef3c7",
  green:"#16a34a",   greenL:"#dcfce7",
  red:"#dc2626",     redL:"#fee2e2",
  blue:"#1d4ed8",    blueL:"#dbeafe",
  ink:"#0f1923", inkM:"#2d3d4f", inkL:"#6b7a8d",
  surface:"#f4f6f5", border:"#e2e8e5",
};

// Score → color palette
export function scoreColors(score) {
  if (score >= 80) return { fg: T.green,   bg: T.greenL,  label: "Strong Match",  emoji: "🎯" };
  if (score >= 65) return { fg: T.primary, bg: T.primaryL,label: "Good Match",    emoji: "✅" };
  if (score >= 45) return { fg: T.amber,   bg: T.amberL,  label: "Fair Match",    emoji: "🔶" };
  return            { fg: T.red,    bg: T.redL,   label: "Low Match",     emoji: "⚠️" };
}

// ─────────────────────────────────────────────────────────────────────────────
// MatchRing — animated SVG score ring with pulsing glow
// ─────────────────────────────────────────────────────────────────────────────
export function MatchRing({ score, size = 80, loading = false }) {
  const [displayed, setDisplayed] = useState(0);
  const [animated, setAnimated]   = useState(false);
  const R = (size - 10) / 2;
  const circ = 2 * Math.PI * R;
  const { fg, label } = scoreColors(score || 0);

  useEffect(() => {
    if (score === null || score === undefined) return;
    setAnimated(false);
    const t = setTimeout(() => {
      setAnimated(true);
      // Count up the number
      let start = 0;
      const step = Math.ceil(score / 30);
      const id = setInterval(() => {
        start += step;
        if (start >= score) { setDisplayed(score); clearInterval(id); }
        else setDisplayed(start);
      }, 30);
      return () => clearInterval(id);
    }, 100);
    return () => clearTimeout(t);
  }, [score]);

  if (loading) return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:T.surface,
      display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <div style={{ width:size*0.4, height:size*0.4, borderRadius:"50%",
        border:`3px solid ${T.border}`, borderTopColor:T.primary,
        animation:"spin .8s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} style={{ transform:"rotate(-90deg)" }}>
        {/* Track */}
        <circle cx={size/2} cy={size/2} r={R} fill="none" stroke={T.surface} strokeWidth={8} />
        {/* Progress */}
        <circle cx={size/2} cy={size/2} r={R} fill="none" stroke={fg} strokeWidth={8}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={animated ? circ * (1 - (score || 0) / 100) : circ}
          style={{ transition: animated ? "stroke-dashoffset 1s cubic-bezier(.22,1,.36,1)" : "none",
            filter:`drop-shadow(0 0 4px ${fg}60)` }} />
      </svg>
      {/* Center text */}
      <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center" }}>
        <span style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:size*0.26, fontWeight:900,
          color:fg, lineHeight:1 }}>{displayed}</span>
        <span style={{ fontSize:size*0.12, fontWeight:700, color:T.inkL, lineHeight:1 }}>%</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MatchBadge — compact inline badge (replaces old one in job cards)
// ─────────────────────────────────────────────────────────────────────────────
export function MatchBadge({ score, isAI = false, loading = false }) {
  if (loading) return (
    <span style={{ fontSize:11, color:T.inkL, fontStyle:"italic" }}>scoring…</span>
  );
  if (score === null || score === undefined) return null;
  const { fg, bg, label, emoji } = scoreColors(score);
  return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:4,
      padding:"3px 10px", borderRadius:100, background:bg, border:`1px solid ${fg}30`,
      cursor:"default" }}
      title={isAI ? "AI-powered match score" : "Keyword match score"}>
      <span style={{ fontSize:10 }}>{emoji}</span>
      <span style={{ fontSize:11, fontWeight:900, color:fg, fontFamily:"'Fraunces',serif" }}>{score}%</span>
      <span style={{ fontSize:10, fontWeight:600, color:fg }}>{label}</span>
      {isAI && <span style={{ fontSize:9, color:fg, opacity:.7 }}>AI</span>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MatchBreakdown — detailed card shown in job detail modal
// ─────────────────────────────────────────────────────────────────────────────
export function MatchBreakdown({ job, profile, studentId }) {
  const [match,    setMatch]   = useState(null);
  const [loading,  setLoading] = useState(true);
  const [upgraded, setUpgraded]= useState(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    if (!job || !profile) return;
    setLoading(true);
    getMatchScore({
      job, profile, studentId,
      onKeywordScore: (kw) => {
        if (!mounted.current) return;
        setMatch(kw);
        setLoading(false);
        setUpgraded(false);
      },
      onAIScore: (ai) => {
        if (!mounted.current) return;
        setMatch(ai);
        setUpgraded(true);
        setLoading(false);
      },
    });
  }, [job?.id, profile?.id]);

  if (loading && !match) return (
    <div style={{ background:T.surface, borderRadius:14, padding:20, display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:32, height:32, borderRadius:"50%", border:`3px solid ${T.border}`,
        borderTopColor:T.primary, animation:"spin .7s linear infinite", flexShrink:0 }} />
      <div>
        <div style={{ fontSize:13, fontWeight:700, color:T.ink }}>Calculating your match…</div>
        <div style={{ fontSize:11, color:T.inkL, marginTop:2 }}>AI is analyzing your profile against this role</div>
      </div>
    </div>
  );

  if (!match) return null;
  const { fg, bg, label, emoji } = scoreColors(match.score);

  return (
    <div style={{ background:bg, border:`1.5px solid ${fg}30`, borderRadius:14, overflow:"hidden" }}>
      {/* Header row */}
      <div style={{ padding:"18px 20px", display:"flex", alignItems:"center", gap:16 }}>
        <MatchRing score={match.score} size={72} />
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:18, fontWeight:900, color:fg }}>
              {emoji} {label}
            </span>
            {upgraded && (
              <span style={{ fontSize:10, fontWeight:700, color:fg, background:`${fg}15`,
                padding:"2px 7px", borderRadius:100 }}>✦ AI</span>
            )}
          </div>
          {match.reasoning && (
            <p style={{ fontSize:12, color:T.inkM, lineHeight:1.6 }}>{match.reasoning}</p>
          )}
          {!match.reasoning && (
            <p style={{ fontSize:12, color:T.inkM }}>
              Based on your skills, course, and location vs. this role's requirements.
            </p>
          )}
        </div>
      </div>

      {/* Score breakdown bar */}
      {match.breakdown && (
        <div style={{ padding:"12px 20px", borderTop:`1px solid ${fg}20`,
          background:"rgba(255,255,255,.5)", display:"flex", gap:8, flexWrap:"wrap" }}>
          {[
            { label:"Skills",   val:match.breakdown.skills,   max:50 },
            { label:"Course",   val:match.breakdown.course,   max:30 },
            { label:"Location", val:match.breakdown.location, max:20 },
          ].map(b => (
            <div key={b.label} style={{ flex:"1 1 80px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, fontWeight:700,
                color:T.inkL, marginBottom:4 }}>
                <span>{b.label}</span>
                <span style={{ color:fg }}>{b.val}/{b.max}</span>
              </div>
              <div style={{ background:"rgba(255,255,255,.6)", borderRadius:100, height:5 }}>
                <div style={{ height:"100%", borderRadius:100, background:fg,
                  width:`${(b.val/b.max)*100}%`, transition:"width .8s" }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Matched skills */}
      {(match.matchedSkills || []).length > 0 && (
        <div style={{ padding:"12px 20px", borderTop:`1px solid ${fg}20` }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.inkL, marginBottom:7 }}>
            ✅ Matched Skills
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {match.matchedSkills.slice(0,8).map(sk => (
              <span key={sk} style={{ fontSize:11, fontWeight:600, color:T.green,
                background:T.greenL, padding:"3px 9px", borderRadius:100,
                border:`1px solid ${T.green}30` }}>
                {sk}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Skill gaps */}
      {(match.skillGaps || []).length > 0 && (
        <div style={{ padding:"12px 20px", borderTop:`1px solid ${fg}20` }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.inkL, marginBottom:7 }}>
            📚 Skills to Develop
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
            {match.skillGaps.slice(0,5).map(sk => (
              <span key={sk} style={{ fontSize:11, fontWeight:600, color:T.amber,
                background:T.amberL, padding:"3px 9px", borderRadius:100,
                border:`1px solid ${T.amber}30` }}>
                {sk}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Strengths */}
      {(match.strengths || []).length > 0 && (
        <div style={{ padding:"12px 20px 16px", borderTop:`1px solid ${fg}20` }}>
          <div style={{ fontSize:11, fontWeight:700, color:T.inkL, marginBottom:7 }}>
            💡 Why You're a Strong Candidate
          </div>
          <ul style={{ paddingLeft:16, margin:0 }}>
            {match.strengths.slice(0,3).map(s => (
              <li key={s} style={{ fontSize:12, color:T.inkM, lineHeight:1.7 }}>{s}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AIApplicantRanker — employer view: rank applicants by AI fit score
// ─────────────────────────────────────────────────────────────────────────────
export function AIApplicantRanker({ applications, job, onRanked }) {
  const [ranking,  setRanking]  = useState(false);
  const [ranked,   setRanked]   = useState(false);
  const [progress, setProgress] = useState(0);

  const runRanking = async () => {
    if (!applications?.length || !job) return;
    setRanking(true);
    setProgress(10);

    try {
      const { rankApplicants } = await import("./AIMatchEngine");
      setProgress(30);
      const sorted = await rankApplicants(
        applications,
        job.title,
        job.description || job.desc || "",
        job.tags || []
      );
      setProgress(100);
      setRanked(true);
      onRanked?.(sorted);
    } catch (err) {
      console.error("Ranking failed:", err);
    } finally {
      setRanking(false);
    }
  };

  if (ranked) return (
    <div style={{ display:"inline-flex", alignItems:"center", gap:6,
      padding:"6px 12px", borderRadius:100, background:T.greenL,
      border:`1px solid ${T.green}30`, fontSize:12 }}>
      <span>✅</span>
      <span style={{ fontWeight:700, color:T.green }}>Ranked by AI fit score</span>
    </div>
  );

  return (
    <button
      onClick={runRanking}
      disabled={ranking || !applications?.length}
      style={{ display:"inline-flex", alignItems:"center", gap:7,
        padding:"8px 16px", borderRadius:10, cursor: ranking ? "wait" : "pointer",
        background: ranking ? T.surface : `linear-gradient(135deg,#063d2b,${T.primary})`,
        color: ranking ? T.inkL : "#fff",
        border:`1.5px solid ${ranking ? T.border : T.primary}`,
        fontFamily:"inherit", fontWeight:700, fontSize:13, transition:"all .2s" }}>
      {ranking ? (
        <>
          <div style={{ width:14, height:14, borderRadius:"50%", border:"2px solid #ccc",
            borderTopColor:T.primary, animation:"spin .7s linear infinite" }} />
          <span>AI ranking {progress}%…</span>
        </>
      ) : (
        <>
          <span>✦</span>
          <span>Rank by AI Fit Score</span>
          <span style={{ fontSize:11, opacity:.8 }}>({applications.length} applicants)</span>
        </>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SmallApplicantScore — compact score badge for applicant list rows
// ─────────────────────────────────────────────────────────────────────────────
export function SmallApplicantScore({ aiRank }) {
  if (!aiRank) return null;
  const { fg, bg } = scoreColors(aiRank.score);
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2, flexShrink:0 }}>
      <div style={{ fontFamily:"'Fraunces',serif", fontSize:16, fontWeight:900, color:fg }}>
        {aiRank.score}%
      </div>
      <div style={{ fontSize:10, fontWeight:600, color:fg, background:bg,
        padding:"1px 7px", borderRadius:100 }}>
        {aiRank.tag || aiRank.recommendation}
      </div>
      {aiRank.reason && (
        <div style={{ fontSize:10, color:T.inkL, maxWidth:140, textAlign:"right", lineHeight:1.4 }}>
          {aiRank.reason}
        </div>
      )}
    </div>
  );
}
